import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  Check,
  MoreVertical,
  Plus,
  Archive,
  ArchiveRestore,
  EyeOff,
  Pencil,
  Trash2,
  AlignJustify,
  List,
  RefreshCw,
  Flag,
  ChevronRight,
  Pin,
  PinOff,
  Loader2,
} from 'lucide-react'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import { toast } from 'sonner'
import { isAbortFetchError } from '@/lib/api/core'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarShellMenuItems,
  useShellMenuHold,
} from '@/components/layout/left-sidebar.panel-menu'
import { SidebarFilterBar } from '@/components/layout/sidebar/SidebarFilterBar'
import { SidebarListRow } from '@/components/layout/sidebar/SidebarListRow'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import { useSidebarListKeyboard } from '@/hooks/use-sidebar-list-keyboard'
import { useWorkspace } from '@/providers/workspace-provider'
import { isThreadWorkspaceVisible } from '@/components/features/inbox/mappers'
import {
  formatThreadActivity,
  groupThreadsByDateRange,
  listAgentChatThreadsPage,
  CHAT_SIDEBAR_SECTION_PREVIEW,
  CHAT_SIDEBAR_DEFAULT_COLLAPSED,
  splitPinnedThreads,
  shouldShowThreadSubtitle,
  touchThreadActivity,
  patchThread,
  deleteThread,
  suggestThreadTitle,
  type AgentChatThread,
  type ChatSidebarSectionKey,
} from '@/lib/api/threads'
import {
  useChatSurfaceRegistry,
  chatConversationTabId,
  shouldPreferAgentPanelForChatOpen,
} from '@/hooks/use-chat-surface-registry'
import type { OpenIntentClickEvent } from '@/lib/open-intent'
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu'
import { testIds } from '@/lib/test-ids'
import { openChatFeedback } from '@/lib/chat-feedback'
import {
  refreshChatsSidebar,
  dismissChatThreadSurfaces,
  chatsSidebarTouchEventName,
} from '@/lib/chats-sidebar-events'
import { rememberThreadWorkspaceMeta } from '@/lib/thread-workspace-meta'
import { orderTaggedWorkspacesForList } from '@/lib/workspace-tag-stack'
import { cn } from '@/lib/utils'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'
import { RenameField } from '@/components/features/rename-field'

/** First-page cache. A remount inside this window paints threads before the refetch. */
const CHATS_SIDEBAR_STALE_MS = 30_000

type ChatsFirstPage = { threads: AgentChatThread[]; hasMore: boolean }

/** Query key for the chats sidebar first page (archived flag and search). */
function chatsSidebarCacheKey(archived: boolean, search: string) {
  return ['chats-sidebar', archived, search] as const
}

/** Cached first page when it is still inside the 30s window, otherwise null. */
function freshChatsPage(queryClient: QueryClient, archived: boolean, search: string): ChatsFirstPage | null {
  const state = queryClient.getQueryState<ChatsFirstPage>(chatsSidebarCacheKey(archived, search))
  if (!state?.data) return null
  if (Date.now() - state.dataUpdatedAt > CHATS_SIDEBAR_STALE_MS) return null
  return state.data
}

/** Production Chats sidebar: thread list, search, new chat, rename. */
export function ChatsSidebarPanel({
  dense = false,
  activeEditorTabId = null,
}: {
  dense?: boolean
  /** Main editor tab id (e.g. chat-conversation-{threadId}) for active row sync. */
  activeEditorTabId?: string | null
}) {
  const registry = useChatSurfaceRegistry()
  const { workspaces, activeWorkspaceIds } = useWorkspace()
  const queryClient = useQueryClient()
  const seeded = freshChatsPage(queryClient, false, '')
  const [threads, setThreads] = useState<AgentChatThread[]>(() => seeded?.threads ?? [])
  const [loading, setLoading] = useState(() => !seeded)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(() => seeded?.hasMore ?? false)
  const [expandedSections, setExpandedSections] = useState<Set<ChatSidebarSectionKey>>(() => new Set())
  const [collapsedSections, setCollapsedSections] = useState<Set<ChatSidebarSectionKey>>(
    () => new Set(CHAT_SIDEBAR_DEFAULT_COLLAPSED),
  )
  const [showArchived, setShowArchived] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null)
  const [renamingValue, setRenamingValue] = useState('')
  const [isSuggestingRename, setIsSuggestingRename] = useState(false)
  const [avoidRenameTitles, setAvoidRenameTitles] = useState<string[]>([])
  const listScrollRef = useRef<HTMLDivElement>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
  const registerTitleRef = useRef(registry.registerThreadTitle)
  registerTitleRef.current = registry.registerThreadTitle

  /** Reload agent_chat threads from the API into sidebar state (first page). */
  const loadThreads = useCallback(async (options?: { silent?: boolean; manual?: boolean }) => {
    if (options?.manual) {
      setRefreshing(true)
    } else if (!options?.silent && !freshChatsPage(queryClient, showArchived, searchQuery.trim())) {
      setLoading(true)
    }
    try {
      const page = await listAgentChatThreadsPage({
        search: searchQuery.trim() || undefined,
        offset: 0,
        status: showArchived ? 'archived' : undefined,
      })
      queryClient.setQueryData(chatsSidebarCacheKey(showArchived, searchQuery.trim()), {
        threads: page.threads,
        hasMore: page.hasMore,
      })
      setThreads(page.threads)
      setHasMore(page.hasMore)
      setExpandedSections(new Set())
      if (!showArchived) {
        setCollapsedSections(new Set(CHAT_SIDEBAR_DEFAULT_COLLAPSED))
      }
      for (const t of page.threads) {
        registerTitleRef.current(t.id, t.title)
        rememberThreadWorkspaceMeta(t)
      }
    } catch (err) {
      if (isAbortFetchError(err)) return
      console.error('[ChatsSidebar] Failed to load threads:', err)
      toast.error('Failed to load chats')
    } finally {
      if (options?.manual) {
        setRefreshing(false)
      } else if (!options?.silent) {
        setLoading(false)
      }
    }
  }, [queryClient, searchQuery, showArchived])

  /** Append the next page when the user scrolls near the list bottom. */
  const loadMoreThreads = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const page = await listAgentChatThreadsPage({
        search: searchQuery.trim() || undefined,
        offset: threads.length,
        status: showArchived ? 'archived' : undefined,
      })
      setHasMore(page.hasMore)
      setThreads((prev) => {
        const seen = new Set(prev.map((t) => t.id))
        const merged = [...prev]
        for (const t of page.threads) {
          if (!seen.has(t.id)) {
            merged.push(t)
            seen.add(t.id)
          }
        }
        return merged
      })
      for (const t of page.threads) {
        registerTitleRef.current(t.id, t.title)
        rememberThreadWorkspaceMeta(t)
      }
    } catch (err) {
      console.error('[ChatsSidebar] Failed to load more threads:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, loadingMore, searchQuery, showArchived, threads.length])

  useEffect(() => {
    const delayMs = searchQuery.trim() ? 300 : 0
    const timer = window.setTimeout(() => {
      void loadThreads()
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [loadThreads, searchQuery])

  useEffect(() => {
    const handler = (e: Event) => {
      const manual = (e as CustomEvent<{ manual?: boolean }>).detail?.manual === true
      void loadThreads({ silent: true, manual })
    }
    window.addEventListener('chats-sidebar-refresh', handler)
    return () => window.removeEventListener('chats-sidebar-refresh', handler)
  }, [loadThreads])

  /** Existing-thread send: bump activity locally. Do not refetch or respinner the counts. */
  useEffect(() => {
    const handler = (e: Event) => {
      const threadId = (e as CustomEvent<{ threadId?: string }>).detail?.threadId
      if (!threadId) return
      const now = new Date().toISOString()
      setThreads((prev) => touchThreadActivity(prev, threadId, now))
    }
    const eventName = chatsSidebarTouchEventName()
    window.addEventListener(eventName, handler)
    return () => window.removeEventListener(eventName, handler)
  }, [])

  /** Sync when user opens the Chats sidebar panel. */
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<{ mode?: string }>).detail?.mode
      if (mode === 'chats') void loadThreads({ silent: true })
    }
    window.addEventListener('flownotes-sidebar-mode', handler as EventListener)
    return () => window.removeEventListener('flownotes-sidebar-mode', handler as EventListener)
  }, [loadThreads])

  useEffect(() => {
    const root = listScrollRef.current
    const sentinel = loadMoreSentinelRef.current
    if (!root || !sentinel || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMoreThreads()
        }
      },
      { root, rootMargin: '120px', threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMoreThreads])

  const expandSection = useCallback((section: ChatSidebarSectionKey) => {
    setExpandedSections((prev) => new Set(prev).add(section))
  }, [])

  const toggleSectionCollapsed = useCallback((section: ChatSidebarSectionKey) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(section)) next.delete(section)
      else next.add(section)
      return next
    })
  }, [])

  const flatThreads = useMemo(() => {
    return threads.filter((t) =>
      isThreadWorkspaceVisible(
        t.workspaceId ?? 'personal',
        activeWorkspaceIds,
        t.taggedOrganizationIds,
      ),
    )
  }, [activeWorkspaceIds, threads])

  const { pinnedThreads, unpinnedThreads } = useMemo(() => {
    const { pinned, unpinned } = splitPinnedThreads(flatThreads)
    return { pinnedThreads: pinned, unpinnedThreads: unpinned }
  }, [flatThreads])

  const grouped = useMemo(
    () => groupThreadsByDateRange(showArchived ? flatThreads : unpinnedThreads),
    [flatThreads, showArchived, unpinnedThreads],
  )

  const streamingThreadId = useMemo(() => {
    if (!registry.streamingConversationId) return null
    return (
      threads.find((t) => t.linkedConversationId === registry.streamingConversationId)?.id ?? null
    )
  }, [registry.streamingConversationId, threads])

  /** Open a thread in the editor or agent panel per registry preference. */
  const handleOpenThread = useCallback(
    (
      thread: AgentChatThread,
      options?: {
        openInNewTab?: boolean
        clickEvent?: OpenIntentClickEvent
        forceAgentPanel?: boolean
        forceEditor?: boolean
      },
    ) => {
      // Hollow "Inbox: Soul update: ..." twins are excluded from the list API. If one still
      // appears (stale client cache), send the user to the real Inbox soul_proposal thread.
      const inboxSourceThreadId =
        typeof thread.metadata?.linked_inbox_thread_id === 'string'
          ? thread.metadata.linked_inbox_thread_id
          : null
      if (inboxSourceThreadId && /^Inbox:\s*/i.test(thread.title || '')) {
        window.dispatchEvent(
          new CustomEvent('open-inbox-thread-main', {
            detail: {
              threadId: inboxSourceThreadId,
              title: (thread.title || '').replace(/^Inbox:\s*/i, ''),
              workspaceId: thread.organizationId ?? thread.workspaceId ?? undefined,
            },
          }),
        )
        return
      }

      registry.registerThreadTitle(thread.id, thread.title)
      const preferAgentPanel =
        !options?.forceEditor
        && !options?.openInNewTab
        && shouldPreferAgentPanelForChatOpen(activeEditorTabId)
      registry.openThread(thread.id, {
        title: thread.title,
        openInNewTab: options?.openInNewTab,
        clickEvent: options?.clickEvent,
        forceAgentPanel: options?.forceAgentPanel ?? preferAgentPanel,
        forceEditor: options?.forceEditor,
      })
    },
    [activeEditorTabId, registry],
  )

  /** Start a new chat via the surface registry. */
  const handleCreateNewChat = useCallback(() => {
    registry.createNewChat({ forceAgentPanel: true })
  }, [registry])

  /** Enter inline rename mode for a sidebar thread row. */
  const startRename = useCallback((thread: AgentChatThread) => {
    setRenamingThreadId(thread.id)
    setRenamingValue(thread.title)
    setAvoidRenameTitles([])
    setIsSuggestingRename(false)
  }, [])

  /** Persist renamed thread title via API and registry. */
  const commitRename = useCallback(async () => {
    if (!renamingThreadId || isSuggestingRename) return
    const title = renamingValue.trim()
    const threadId = renamingThreadId
    setRenamingThreadId(null)
    if (!title) return
    try {
      await patchThread(threadId, { title })
      registry.updateTabTitle(threadId, title)
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, title } : t)),
      )
    } catch (err) {
      console.error('[ChatsSidebar] Rename failed:', err)
      toast.error('Failed to rename chat')
    }
  }, [registry, renamingThreadId, renamingValue, isSuggestingRename])

  const suggestRename = useCallback(async () => {
    if (!renamingThreadId) return
    setIsSuggestingRename(true)
    try {
      const result = await suggestThreadTitle(renamingThreadId, {
        avoid: avoidRenameTitles,
      })
      if (result.title) {
        setRenamingValue(result.title)
        setAvoidRenameTitles((prev) => [...prev, result.title].slice(-8))
      }
    } catch (err) {
      console.error('[ChatsSidebar] Suggest rename failed:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to suggest title')
    } finally {
      setIsSuggestingRename(false)
    }
  }, [renamingThreadId, avoidRenameTitles])

  /** Close open editor tab / agent panel when a thread is archived or deleted. */
  const dismissThreadFromSurfaces = useCallback(
    (threadId: string) => {
      dismissChatThreadSurfaces(threadId)
      if (registry.agentPanelThreadId === threadId) {
        registry.setAgentPanelThreadId(null)
      }
    },
    [registry],
  )

  /** Mark thread unread via PATCH /api/threads/:id. */
  const handleMarkUnread = useCallback(async (thread: AgentChatThread) => {
    if (thread.status === 'unread') return
    try {
      await patchThread(thread.id, { status: 'unread' })
      setThreads((prev) =>
        prev.map((t) => (t.id === thread.id ? { ...t, status: 'unread' } : t)),
      )
      toast.success('Marked as unread')
    } catch (err) {
      console.error('[ChatsSidebar] Mark unread failed:', err)
      toast.error('Failed to mark as unread')
    }
  }, [])

  /** Archive thread (hidden from default sidebar list). */
  const handleArchive = useCallback(
    async (thread: AgentChatThread) => {
      try {
        await patchThread(thread.id, { status: 'archived' })
        dismissThreadFromSurfaces(thread.id)
        setThreads((prev) => prev.filter((t) => t.id !== thread.id))
        toast.success('Chat archived')
      } catch (err) {
        console.error('[ChatsSidebar] Archive failed:', err)
        toast.error('Failed to archive chat')
      }
    },
    [dismissThreadFromSurfaces],
  )

  /** Restore thread from archive back to the main list. */
  const handleUnarchive = useCallback(
    async (thread: AgentChatThread) => {
      try {
        await patchThread(thread.id, { status: 'read' })
        setThreads((prev) => prev.filter((t) => t.id !== thread.id))
        toast.success('Chat restored')
      } catch (err) {
        console.error('[ChatsSidebar] Unarchive failed:', err)
        toast.error('Failed to restore chat')
      }
    },
    [],
  )

  /** Pin or unpin a thread at the top of the sidebar. */
  const handleTogglePin = useCallback(async (thread: AgentChatThread) => {
    const nextPinned = !thread.pinned
    try {
      await patchThread(thread.id, { pinned: nextPinned })
      setThreads((prev) =>
        prev.map((t) => (t.id === thread.id ? { ...t, pinned: nextPinned } : t)),
      )
      toast.success(nextPinned ? 'Chat pinned' : 'Chat unpinned')
    } catch (err) {
      console.error('[ChatsSidebar] Pin toggle failed:', err)
      toast.error(nextPinned ? 'Failed to pin chat' : 'Failed to unpin chat')
    }
  }, [])

  /** Permanently delete thread after confirmation. */
  const handleDelete = useCallback(
    async (thread: AgentChatThread) => {
      const confirmed = window.confirm(
        `Delete "${thread.title}"? This cannot be undone.`,
      )
      if (!confirmed) return
      try {
        await deleteThread(thread.id)
        registry.forgetAgentPanelThread(thread.id)
        dismissThreadFromSurfaces(thread.id)
        setThreads((prev) => prev.filter((t) => t.id !== thread.id))
        toast.success('Chat deleted')
      } catch (err) {
        console.error('[ChatsSidebar] Delete failed:', err)
        toast.error('Failed to delete chat')
      }
    },
    [dismissThreadFromSurfaces, registry],
  )

  const buildContextMenuEntries = useCallback(
    (thread: AgentChatThread): EntityContextMenuEntry[] => {
      const entries: EntityContextMenuEntry[] = [
        {
          label: 'Open in new tab',
          icon: OpenDestinationIcons.openInNewTab,
          previewPanel: 'main',
          onSelect: () => {
            handleOpenThread(thread, {
              openInNewTab: true,
              forceEditor: true,
            })
          },
        },
        {
          label: 'Open in agent panel',
          icon: OpenDestinationIcons.openInAgentPanel,
          previewPanel: 'agent',
          onSelect: () => {
            handleOpenThread(thread, { forceAgentPanel: true })
          },
        },
        { separator: true },
        {
          label: thread.pinned ? 'Unpin' : 'Pin',
          icon: thread.pinned ? PinOff : Pin,
          onSelect: () => void handleTogglePin(thread),
        },
        {
          label: 'Rename',
          icon: Pencil,
          onSelect: () => startRename(thread),
        },
        {
          label: 'Mark as unread',
          icon: EyeOff,
          disabled: thread.status === 'unread',
          onSelect: () => void handleMarkUnread(thread),
        },
        { separator: true },
      ]

      if (showArchived) {
        entries.push({
          label: 'Restore',
          icon: ArchiveRestore,
          onSelect: () => void handleUnarchive(thread),
        })
      } else {
        entries.push({
          label: 'Archive',
          icon: Archive,
          onSelect: () => void handleArchive(thread),
        })
      }

      entries.push({
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onSelect: () => void handleDelete(thread),
      })

      return entries
    },
    [
      handleArchive,
      handleDelete,
      handleMarkUnread,
      handleOpenThread,
      handleTogglePin,
      handleUnarchive,
      showArchived,
      startRename,
    ],
  )

  const renderThreadRow = useCallback(
    (thread: AgentChatThread, section: ChatSidebarSectionKey) => {
      const binding = registry.getBinding(thread.id)
      const isOpenInEditor = binding?.surface === 'editor'
      const hostedInAgent = registry.agentPanelThreadId === thread.id
      const isEditorChatActive = activeEditorTabId === chatConversationTabId(thread.id)
      const isAgentChatActive = hostedInAgent && registry.agentPanelExpanded
      const isActive = isEditorChatActive || isAgentChatActive
      const isFocused = registry.focusedThreadId === thread.id
      const isRenaming = renamingThreadId === thread.id
      const isStreaming = streamingThreadId === thread.id
      const isUnread = thread.status === 'unread'
      const showSubtitle = shouldShowThreadSubtitle(section, { dense })
      const rowDense = dense || !showSubtitle
      const taggedOrgIds = thread.taggedOrganizationIds ?? []
      const taggedWorkspaces = orderTaggedWorkspacesForList(
        taggedOrgIds,
        workspaces,
        activeWorkspaceIds,
      )
      const fallbackWorkspace = thread.workspaceId
        ? workspaces.find((w) => w.id === thread.workspaceId)
        : null
      const primaryWorkspace = taggedWorkspaces[0] ?? (
        fallbackWorkspace
          ? { id: fallbackWorkspace.id, name: fallbackWorkspace.name, color: fallbackWorkspace.color }
          : null
      )

      return (
        <SidebarListRow
          key={thread.id}
          state={{
            open: isOpenInEditor || hostedInAgent,
            focused: isFocused,
            active: isActive,
          }}
          workspaceColor={primaryWorkspace?.color}
          workspaceName={taggedWorkspaces.map((w) => w.name).join(', ') || primaryWorkspace?.name}
          workspaceTags={taggedWorkspaces}
          workspaceTagStackTestId={testIds.chatsSidebar.workspaceTags(thread.id)}
          dense={rowDense}
          onActivate={(e) =>
            handleOpenThread(thread, {
              clickEvent: { metaKey: e.metaKey, ctrlKey: e.ctrlKey },
            })
          }
          onDoubleClick={(e) => {
            e.preventDefault()
            handleOpenThread(thread, {
              openInNewTab: true,
              forceEditor: true,
              clickEvent: { metaKey: true, ctrlKey: false },
            })
            window.dispatchEvent(
              new CustomEvent('pin-tab-by-id', {
                detail: { tabId: chatConversationTabId(thread.id) },
              }),
            )
          }}
          onMouseEnter={() => registry.setFocusedThreadId(thread.id)}
          onMouseLeave={() => {
            requestAnimationFrame(() => {
              const chatPaneFocused = Boolean(
                document.activeElement?.closest(
                  `[data-testid="${testIds.chatPane.container}"]`,
                ),
              )
              if (registry.focusedThreadId === thread.id && !chatPaneFocused) {
                registry.setFocusedThreadId(null)
              }
            })
          }}
          data-testid={testIds.chatsSidebar.item(thread.id)}
          contextMenuTestId={testIds.chatsSidebar.contextMenu}
          contextMenuEntries={buildContextMenuEntries(thread)}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {thread.pinned && !showArchived ? (
              <Pin
                className="h-3 w-3 shrink-0 text-muted-foreground"
                aria-hidden
              />
            ) : null}
            {isStreaming ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-primary animate-pulse"
                title="AI is responding"
                aria-label="AI is responding"
              />
            ) : null}
            {!isStreaming && isUnread ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-primary"
                title="Unread"
                aria-label="Unread"
              />
            ) : null}
            {isRenaming ? (
              <div
                className="min-w-0 flex-1"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <RenameField
                  value={renamingValue}
                  onChange={setRenamingValue}
                  onSuggest={() => void suggestRename()}
                  isSuggesting={isSuggestingRename}
                  showSuggest={Boolean(thread.linkedConversationId)}
                  suggestLabel="AI suggest title (click again to shuffle)"
                  autoFocus
                  inputClassName="h-8 py-1 font-sans text-sm"
                  onBlur={(e) => {
                    const related = e.relatedTarget as HTMLElement | null
                    if (related?.closest(`[data-testid="${testIds.renameField.root}"]`)) {
                      return
                    }
                    void commitRename()
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void commitRename()
                    }
                    if (e.key === 'Escape') {
                      setRenamingThreadId(null)
                    }
                  }}
                />
              </div>
            ) : (
              <p
                className={cn(
                  'min-w-0 flex-1 truncate text-sm',
                  isUnread ? 'font-semibold' : 'font-medium',
                )}
              >
                {thread.title}
              </p>
            )}
          </div>
          {showSubtitle ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatThreadActivity(thread.lastMessageAt ?? thread.updatedAt)}
            </p>
          ) : null}
        </SidebarListRow>
      )
    },
    [
      activeEditorTabId,
      activeWorkspaceIds,
      buildContextMenuEntries,
      commitRename,
      dense,
      handleOpenThread,
      isSuggestingRename,
      registry,
      renamingThreadId,
      renamingValue,
      showArchived,
      streamingThreadId,
      suggestRename,
      workspaces,
    ],
  )

  const renderThreadSection = useCallback(
    (
      sectionKey: ChatSidebarSectionKey,
      label: string,
      items: AgentChatThread[],
      options?: { previewLimit?: number },
    ) => {
      if (items.length === 0) return null

      const previewLimit = options?.previewLimit ?? CHAT_SIDEBAR_SECTION_PREVIEW
      const isSectionExpanded = expandedSections.has(sectionKey)
      const visibleItems = isSectionExpanded ? items : items.slice(0, previewLimit)
      const hiddenCount = items.length - visibleItems.length
      const showSectionMore = hiddenCount > 0
      const isCollapsed = collapsedSections.has(sectionKey)

      return (
        <div
          key={sectionKey}
          className="mb-2"
          data-testid={
            sectionKey === 'pinned' ? testIds.chatsSidebar.pinnedSection : undefined
          }
        >
          <button
            type="button"
            onClick={() => toggleSectionCollapsed(sectionKey)}
            className="mb-1 flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            data-testid={testIds.chatsSidebar.sectionHeader(sectionKey)}
            aria-expanded={!isCollapsed}
          >
            <ChevronRight
              className={cn(
                'h-3 w-3 shrink-0 transition-transform duration-150',
                !isCollapsed && 'rotate-90',
              )}
            />
            <span className="truncate">{label}</span>
            <span className="ml-auto tabular-nums text-[10px] font-normal normal-case">
              {items.length}
            </span>
          </button>
          {!isCollapsed ? (
            <div className="space-y-1">
              {visibleItems.map((thread) => renderThreadRow(thread, sectionKey))}
              {showSectionMore ? (
                <button
                  type="button"
                  onClick={() => expandSection(sectionKey)}
                  className="w-full rounded-md px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  data-testid={testIds.chatsSidebar.sectionMore(sectionKey)}
                >
                  ... More
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )
    },
    [
      collapsedSections,
      expandSection,
      expandedSections,
      renderThreadRow,
      toggleSectionCollapsed,
    ],
  )

  useSidebarListKeyboard({
    items: flatThreads,
    focusedId: registry.focusedThreadId,
    setFocusedId: registry.setFocusedThreadId,
    onOpen: (thread, event) => {
      handleOpenThread(thread, {
        clickEvent: event,
        openInNewTab: Boolean(event?.metaKey || event?.ctrlKey),
      })
    },
    onArchive: (thread) => void handleArchive(thread),
    onDelete: (thread) => void handleDelete(thread),
    enabled: !renamingThreadId,
    containerRef: listScrollRef,
  })

  return (
    <div
      className="flex h-full flex-col min-h-0"
      data-testid={testIds.chatsSidebar.container}
    >
      <SidebarFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search chats..."
        searchTestId={testIds.chatsSidebar.search}
        trailingSlot={
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0 gap-1 px-2"
            onClick={handleCreateNewChat}
            data-testid={testIds.chatsSidebar.newChat}
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </Button>
        }
      />

      <div
        ref={listScrollRef}
        className="flex-1 overflow-auto p-2 min-h-0"
        data-testid={testIds.chatsSidebar.list}
      >
        {loading && threads.length === 0 && (
          <p className="px-2 py-4 text-xs text-muted-foreground">Loading chats...</p>
        )}
        {!loading && threads.length === 0 && (
          <SidebarEmptyState
            title={showArchived ? 'No archived chats' : 'No chats yet'}
            description={
              showArchived
                ? 'Archived chats appear here.'
                : 'Start a new chat.'
            }
            className="py-8"
          />
        )}
        {!loading && threads.length > 0 && grouped.length === 0 && !showArchived && pinnedThreads.length === 0 && (
          <SidebarEmptyState
            title="No chats in scope"
            description="Enable workspaces in the icon bar picker."
            className="py-8"
          />
        )}
        {!showArchived && pinnedThreads.length > 0
          ? renderThreadSection('pinned', 'Pinned', pinnedThreads, { previewLimit: 20 })
          : null}
        {grouped.map((group) =>
          renderThreadSection(group.bucket, group.label, group.items),
        )}
        {loadingMore ? (
          <div
            className="flex justify-center py-2"
            data-testid={testIds.chatsSidebar.loadMoreLoading}
            aria-live="polite"
          >
            <Loader2
              className="h-3 w-3 animate-spin text-muted-foreground"
              aria-label="Loading more chats"
            />
          </div>
        ) : null}
        {hasMore ? (
          <div
            ref={loadMoreSentinelRef}
            className="h-px w-full shrink-0 overflow-hidden"
            data-testid={testIds.chatsSidebar.loadMoreSentinel}
            aria-hidden
          />
        ) : null}
        <button
          type="button"
          onClick={() => setShowArchived((prev) => !prev)}
          className={cn(
            'mt-2 flex w-full items-center justify-between rounded-md px-2 py-2 text-sm transition-colors',
            showArchived
              ? 'bg-muted/60 text-foreground'
              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
          data-testid={testIds.chatsSidebar.archivedToggle}
        >
          <span className="flex items-center gap-2">
            <Archive className="h-3.5 w-3.5" />
            {showArchived ? 'Back to chats' : 'Archived'}
          </span>
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

    </div>
  )
}

/** Chats panel header menu (options only; scope is on the icon bar). */
export function ChatsSidebarPanelMenu({
  dense,
  onDenseChange,
  openTabsVisible,
  onOpenTabsVisibleChange,
  collapsed,
  onCollapseSidebar,
}: {
  dense?: boolean
  onDenseChange?: (dense: boolean) => void
  openTabsVisible: boolean
  onOpenTabsVisibleChange: (visible: boolean) => void
  collapsed: boolean
  onCollapseSidebar: () => void
}) {
  const registry = useChatSurfaceRegistry()
  const onMenuOpenChange = useShellMenuHold()

  return (
    <DropdownMenu onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-6 shrink-0 items-center gap-1 rounded px-1',
            'text-sidebar-foreground/60 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
          )}
          title="Chats options"
          aria-label="Chats options"
          data-testid={testIds.chatsSidebar.panelMenuTrigger}
        >
          <MoreVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        data-testid={testIds.chatsSidebar.panelMenu}
      >
        {onDenseChange !== undefined ? (
          <>
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              Layout
            </DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => onDenseChange(false)} className={cn(!dense && 'text-primary')}>
              <AlignJustify className="mr-2 h-4 w-4" />
              Comfortable
              {!dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onDenseChange(true)} className={cn(dense && 'text-primary')}>
              <List className="mr-2 h-4 w-4" />
              Compact
              {dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem onSelect={() => refreshChatsSidebar(true)}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh list
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => openChatFeedback({ category: 'feedback' })}
          data-testid={testIds.chatsSidebar.feedbackMenuItem}
        >
          <Flag className="mr-2 h-4 w-4" />
          Report issue or feedback
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => registry.setOpenInEditor(!registry.openInEditor)}>
          <span className="flex w-4 shrink-0 items-center justify-center mr-2">
            {registry.openInEditor ? <Check className="h-3.5 w-3.5" /> : null}
          </span>
          Open chats in tabs
        </DropdownMenuItem>
        <SidebarShellMenuItems
          openTabsVisible={openTabsVisible}
          onOpenTabsVisibleChange={onOpenTabsVisibleChange}
          collapsed={collapsed}
          onCollapseSidebar={onCollapseSidebar}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
